import React, { forwardRef } from 'react';
import { Menu as MantineMenu, MenuProps, useMantineTheme, MantineSize } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedMenuProps extends MenuProps {
  gradientHover?: boolean;
  gradientVariant?: GradientVariant;
  responsiveBreakpoint?: number | MantineSize;
}

const BaseMenu = MantineMenu as any;

const MenuBase = forwardRef<HTMLDivElement, ThemedMenuProps>(
  ({ gradientHover = false, gradientVariant = 'primary', styles, children, responsiveBreakpoint = 'sm', ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];
    const breakpointValue =
      typeof responsiveBreakpoint === 'number'
        ? responsiveBreakpoint
        : theme.breakpoints?.[responsiveBreakpoint as MantineSize];
    const isSmallScreen = breakpointValue ? useMediaQuery(`(max-width: ${breakpointValue}px)`) : false;

    const commonProps: Partial<MenuProps> = {
      position: isSmallScreen ? 'bottom-start' : rest.position,
      width: isSmallScreen ? 'target' : rest.width,
      transitionProps: { duration: rest.transitionProps?.duration ?? (isSmallScreen ? 100 : 150) },
    };

    if (!gradientHover) {
      return (
        <BaseMenu ref={ref} styles={styles} {...commonProps} {...rest}>
          {children}
        </BaseMenu>
      );
    }

    return (
      <BaseMenu
        ref={ref}
        styles={{
          ...styles,
          item: {
            '&[data-hovered]': {
              backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              color: 'white',
            },
            ...(styles as any)?.item,
          },
        }}
        {...commonProps}
        {...rest}
      >
        {children}
      </BaseMenu>
    );
  }
);

MenuBase.displayName = 'Menu';

type MenuComponent = React.ForwardRefExoticComponent<ThemedMenuProps & React.RefAttributes<HTMLDivElement>> & {
  Target: typeof MantineMenu.Target;
  Dropdown: typeof MantineMenu.Dropdown;
  Item: typeof MantineMenu.Item;
  Label: typeof MantineMenu.Label;
  Divider: typeof MantineMenu.Divider;
};

const Menu = Object.assign(MenuBase, {
  Target: MantineMenu.Target,
  Dropdown: MantineMenu.Dropdown,
  Item: MantineMenu.Item,
  Label: MantineMenu.Label,
  Divider: MantineMenu.Divider,
}) as MenuComponent;

export default Menu;
